import { useState, useRef, useEffect, useCallback } from "react";
import FaiBox from "../../../MuiComponents/FaiBox";
import FaiTypography from "../../../MuiComponents/FaiTypography";
import theme from "../../../assets/theme";
import { Form, Formik, FormikProps } from "formik";
import { Grid } from "@mui/material";
import FormField from "../../../components/FormField";
import FaiButton from "../../../MuiComponents/FaiButton";
import FaiSelect from "../../../MuiComponents/FaiSelect";
import { merchantAuthController } from "../../../controllers/authController";
import { useNavigate } from "react-router-dom";
import * as Yup from "yup";
import commonController from "../../../controllers/commonController";
import { useAuth } from "../../../context/AuthContext";
import { useProfile } from "../../../context/ProfileContext";
import { toast } from "react-toastify";

interface InitialObject {
	isDraft: boolean;
	pickupLocationNickName: string;
	shipperName: string;
	phone: string;
	address: string;
	city: string;
	state: string;
	country: string;
	pinCode: string;
}

interface CountryOption {
	value: string;
	label: string;
	id: number;
}

interface StateOption {
	value: string;
	label: string;
	id: number;
}

interface CityOption {
	value: string;
	label: string;
	id: number;
}

// Validation schema
const LogisticDetailsSchema = Yup.object().shape({
	pickupLocationNickName: Yup.string().required(
		"Pickup location nickname is required"
	),
	shipperName: Yup.string().required("Shipper name is required"),
	phone: Yup.string()
		.matches(/^[0-9]{10}$/, "Phone number must be 10 digits")
		.required("Phone number is required"),
	address: Yup.string().required("Address is required"),
	state: Yup.string().required("State is required"),
	country: Yup.string().required("Country is required"),
	city: Yup.string().required("City is required"),
	pinCode: Yup.string()
		.matches(/^[0-9]{6}$/, "Pin code must be 6 digits")
		.required("Pin code is required"),
});

function LogisticDetails() {
	const { typography, palette, borders } = theme;
	const { profile, setProfile } = useProfile();

	const { setStep } = useAuth();
	const [initialObject, setInitialObject] = useState<InitialObject>({
		isDraft: false,
		pickupLocationNickName:
			profile?.logisticsInformation?.pickupLocationNickName || "",
		shipperName: profile?.logisticsInformation?.shipperName || "",
		phone: profile?.logisticsInformation?.phone || "",
		address: profile?.logisticsInformation?.address || "",
		city: profile?.logisticsInformation?.city || "",
		state: profile?.logisticsInformation?.state || "",
		country: profile?.logisticsInformation?.country || "",
		pinCode: profile?.logisticsInformation?.pinCode || "",
	});

	const [countries, setCountries] = useState<CountryOption[]>([]);
	const [states, setStates] = useState<StateOption[]>([]);
	const [cities, setCities] = useState<CityOption[]>([]);
	const [loading, setLoading] = useState(true);
	const navigate = useNavigate();

	// Update form values when profile is loaded/changed
	useEffect(() => {
		if (profile?.logisticsInformation) {
			setInitialObject({
				isDraft: false,
				pickupLocationNickName: profile.logisticsInformation.pickupLocationNickName || "",
				shipperName: profile.logisticsInformation.shipperName || "",
				phone: profile.logisticsInformation.phone || "",
				address: profile.logisticsInformation.address || "",
				city: profile.logisticsInformation.city || "",
				state: profile.logisticsInformation.state || "",
				country: profile.logisticsInformation.country || "",
				pinCode: profile.logisticsInformation.pinCode || "",
			});
		}
	}, [profile]);

	useEffect(() => {
		const getCountries = async () => {
			try {
				setLoading(true);
				const response = await commonController.getCountry();

				// Format countries for dropdown
				const countryOptions = response.data?.data?.data?.map(
					(country: any) => ({
						value: country.name,
						label: country.name,
						id: country.id,
					})
				);

				setCountries(countryOptions);
			} catch (error) {
				console.error("Error fetching countries:", error);
			} finally {
				setLoading(false);
			}
		};

		getCountries();
	}, []);

	const getStates = async (countryId: number) => {
		try {
			setLoading(true);
			const response = await commonController.getState(countryId);

			const stateOptions = response.data.data.data.map((state: any) => ({
				value: state.name,
				label: state.name,
				id: state.id,
			}));

			setStates(stateOptions);
			setCities([]); // Reset cities when country changes
		} catch (error) {
			console.error("Error fetching states:", error);
		} finally {
			setLoading(false);
		}
	};

	const getCities = async (stateId: number) => {
		try {
			setLoading(true);
			const response = await commonController.getCity(stateId);

			const cityOptions = response.data.data.data.map((city: any) => ({
				value: city.name,
				label: city.name,
				id: city.id,
			}));

			setCities(cityOptions);
		} catch (error) {
			console.error("Error fetching cities:", error);
		} finally {
			setLoading(false);
		}
	};

	const handleSubmit = async (
		values: InitialObject,
		{ setSubmitting }: any
	) => {
		try {
			const { data } = await merchantAuthController.setLogisticsInfo({
				...values,
				pinCode: parseInt(values.pinCode),
				lat: 19.076, // Static latitude value
				lng: "72.8777", // Static longitude value
			});

			if (data.status === 200 || data.status === 201) {
				// Update the profile context with the new logistics information
				setProfile({
					...profile,
					logisticsInformation: {
						pickupLocationNickName: values.pickupLocationNickName,
						shipperName: values.shipperName,
						phone: values.phone,
						address: values.address,
						city: values.city,
						state: values.state,
						country: values.country,
						pinCode: values.pinCode,
					},
				});

				toast.success(data.message || "Logistics details updated successfully");
				if (!profile?.logisticsInformation) {
					// Clear the step to show the sidebar menu
					localStorage.removeItem("step");
					setStep(undefined);
					navigate("/app/onboarding/home");
				}
			}
		} catch (error) {
			console.error("Error updating logistics details:", error);
		} finally {
			setSubmitting(false);
		}
	};

	return (
		<>
			<FaiBox>
				<FaiBox
					bgColor={palette.white.main}
					className={"logistics-details"}
					mt={"15px"}
					p={"15px"}
					borderRadius={borders.borderRadius.md}
				>
					<FaiBox>
						<FaiTypography
							variant="h4"
							fontWeight={typography.fontWeightMedium}
						>
							Logistics Details
						</FaiTypography>
						<FaiTypography variant="h6" mb={"21px"}>
							Please provide your logistics details to complete your profile.
						</FaiTypography>
					</FaiBox>

					<Formik
						initialValues={initialObject}
						enableReinitialize={true}
						validateOnBlur={true}
						validateOnChange={true}
						validationSchema={LogisticDetailsSchema}
						onSubmit={handleSubmit}
					>
						{(props: FormikProps<InitialObject>) => {
							const {
								handleSubmit,
								setFieldValue,
								handleChange,
								handleBlur,
								values,
								touched,
								errors,
							} = props;
							return (
								<Form>
									<Grid container spacing={2}>
										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Pickup Location Nickname"}
													name={"pickupLocationNickName"}
													placeholder={"Enter pickup location nickname"}
													value={values.pickupLocationNickName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.pickupLocationNickName &&
														Boolean(errors.pickupLocationNickName)
													}
													helperText={
														touched.pickupLocationNickName &&
														errors.pickupLocationNickName
													}
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Shipper Name"}
													name={"shipperName"}
													placeholder={"Enter shipper name"}
													value={values.shipperName}
													onChange={handleChange}
													onBlur={handleBlur}
													error={
														touched.shipperName && Boolean(errors.shipperName)
													}
													helperText={touched.shipperName && errors.shipperName}
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Phone Number"}
													name={"phone"}
													placeholder={"Enter phone number"}
													value={values.phone}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.phone && Boolean(errors.phone)}
													helperText={touched.phone && errors.phone}
												/>
											</FaiBox>
										</Grid>

										<Grid item sm={12} xs={12}>
											<FaiBox mb={1}>
												<FormField
													multiline
													rows={5}
													label={"Address"}
													name={"address"}
													placeholder={"Enter address"}
													value={values.address}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.address && Boolean(errors.address)}
													helperText={touched.address && errors.address}
												/>
											</FaiBox>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox
												mb={1}
												ml={0.5}
												lineHeight={0}
												display="inline-block"
											>
												<FaiTypography
													component="label"
													variant="caption"
													fontWeight="bold"
													textTransform="capitalize"
												>
													Country
												</FaiTypography>
											</FaiBox>
											<FaiSelect
												placeholder={"Select Country"}
												name={"country"}
												value={
													values.country
														? { value: values.country, label: values.country }
														: null
												}
												onChange={(selectedOption: any) => {
													setFieldValue(
														"country",
														selectedOption ? selectedOption.value : ""
													);
													setFieldValue("state", "");
													setFieldValue("city", "");
													if (selectedOption) {
														const country = countries.find(
															(c) => c.value === selectedOption.value
														);
														if (country) {
															getStates(country.id);
														}
													}
												}}
												options={countries}
												error={touched.country && Boolean(errors.country)}
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox
												mb={1}
												ml={0.5}
												lineHeight={0}
												display="inline-block"
											>
												<FaiTypography
													component="label"
													variant="caption"
													fontWeight="bold"
													textTransform="capitalize"
												>
													State
												</FaiTypography>
											</FaiBox>
											<FaiSelect
												placeholder={"Select State"}
												name={"state"}
												value={
													values.state
														? { value: values.state, label: values.state }
														: null
												}
												onChange={(selectedOption: any) => {
													setFieldValue(
														"state",
														selectedOption ? selectedOption.value : ""
													);
													setFieldValue("city", "");
													if (selectedOption) {
														const state = states.find(
															(s) => s.value === selectedOption.value
														);
														if (state) {
															getCities(state.id);
														}
													}
												}}
												options={states}
												error={touched.state && Boolean(errors.state)}
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox
												mb={1}
												ml={0.5}
												lineHeight={0}
												display="inline-block"
											>
												<FaiTypography
													component="label"
													variant="caption"
													fontWeight="bold"
													textTransform="capitalize"
												>
													City
												</FaiTypography>
											</FaiBox>
											<FaiSelect
												placeholder={"Select City"}
												name={"city"}
												value={
													values.city
														? { value: values.city, label: values.city }
														: null
												}
												onChange={(selectedOption: any) => {
													setFieldValue(
														"city",
														selectedOption ? selectedOption.value : ""
													);
												}}
												options={cities}
												error={touched.city && Boolean(errors.city)}
											/>
										</Grid>

										<Grid item xs={12} sm={6}>
											<FaiBox xs={12} sm={6}>
												<FormField
													label={"Pin Code"}
													name={"pinCode"}
													placeholder={"Enter pin code"}
													value={values.pinCode}
													onChange={handleChange}
													onBlur={handleBlur}
													error={touched.pinCode && Boolean(errors.pinCode)}
													helperText={touched.pinCode && errors.pinCode}
												/>
											</FaiBox>
										</Grid>
									</Grid>

									<Grid container justifyContent="flex-end" mt={2} spacing={2}>
										<Grid item sm={2} xs={12}>
											<FaiButton
												color="primary"
												variant={"contained"}
												fullWidth
												type="submit"
												disabled={props.isSubmitting}
											>
												{props.isSubmitting ? "Submitting..." : "Submit"}
											</FaiButton>
										</Grid>
									</Grid>
								</Form>
							);
						}}
					</Formik>
				</FaiBox>
			</FaiBox>
		</>
	);
}

export default LogisticDetails;
